import React, {useEffect, useRef, useState} from 'react';

import {Loader} from '@googlemaps/js-api-loader';
import {GoogleMap as GM, Marker} from '@react-google-maps/api';
import {useTranslation} from 'react-i18next';

import {ProductGroupIcon} from 'components/common/ProductGroupIcon';
import {Button, ButtonVariant} from 'components/ui-kit/Button';
import {Input} from 'components/ui-kit/Input';
import {Select} from 'components/ui-kit/Select';
import {Spinner} from 'components/ui-kit/Spinner';
import {MAP_CENTER} from 'core/constants/env';
import {ProductGroup} from 'core/constants/productGroups';
import {MovementFilters} from 'types/geo';

import {markerSvg} from './marker';
import {eventColor} from './meta';
import {
    MapWrapper,
    MapTitle,
    MapDescription,
    MapCanvas,
    Popup,
    FilterRow,
    FilterActions,
    ProductGroupLabel,
    Legend,
} from './styled';
import {useMovementMap} from './useMovementMap';

const loader = new Loader({
    apiKey: process.env.REACT_APP_GOOGLE_MAPS_KEY || '',
    version: 'weekly',
    language: 'en',
});
const containerStyle = {width: '100%', height: '500px'};

export const GoogleMap = () => {
    const {t} = useTranslation();
    const data = useMovementMap();
    const [loaded, setLoaded] = useState(false);
    const [loadError, setLoadError] = useState(false);
    const [draft, setDraft] = useState<MovementFilters>({});
    const map = useRef<google.maps.Map | null>(null);
    useEffect(() => {
        let cancelled = false;
        loader
            .importLibrary('maps')
            .then(() => {
                if (!cancelled) setLoaded(true);
            })
            .catch(() => {
                if (!cancelled) setLoadError(true);
            });
        return () => {
            cancelled = true;
        };
    }, []);
    const eventLabel = (code: string, title?: string) =>
        t('movementMap.eventKinds.' + code, {defaultValue: title || code});
    const eventOptions = data.dictionaries.eventKinds.map(({code, title}) => ({
        value: code,
        label: (
            <span>
                <span style={{color: eventColor(code)}}>■ </span>
                {eventLabel(code, title)}
            </span>
        ),
    }));
    const groupOptions = data.dictionaries.productGroups.map((code) => ({
        value: code,
        label: (
            <ProductGroupLabel>
                <ProductGroupIcon
                    value={code as ProductGroup}
                    width={10}
                    style={{marginBottom: 0}}
                />
                <span>{t('dictionaries.productGroups.' + code, {defaultValue: code})}</span>
            </ProductGroupLabel>
        ),
    }));
    const maximum = Math.max(1, ...data.clusters.map((c) => c.eventsCount));
    const count = (n: number) => n.toLocaleString();
    return (
        <MapWrapper>
            <MapTitle>{t('movementMap.title')}</MapTitle>
            <MapDescription>{t('movementMap.description')}</MapDescription>
            <FilterRow
                onSubmit={(event) => {
                    event.preventDefault();
                    data.setFilters({
                        ...draft,
                        productCode: draft.productCode?.trim() || undefined,
                    });
                }}
            >
                <Select
                    size="sm"
                    label={t('movementMap.eventKind')}
                    options={eventOptions}
                    isClearable
                    placeholder={t('movementMap.allEvents')}
                    value={eventOptions.find((o) => o.value === draft.eventKind) || null}
                    onChange={(option: {value: string} | null) =>
                        setDraft({...draft, eventKind: option?.value})
                    }
                />
                <Input
                    size="sm"
                    label={t('movementMap.productCode')}
                    value={draft.productCode || ''}
                    onChange={(event) => setDraft({...draft, productCode: event.target.value})}
                />
                <Select
                    size="sm"
                    label={t('movementMap.productGroup')}
                    options={groupOptions}
                    isClearable
                    placeholder={t('movementMap.allGroups')}
                    value={groupOptions.find((o) => o.value === draft.productGroup) || null}
                    onChange={(option: {value: string} | null) =>
                        setDraft({...draft, productGroup: option?.value})
                    }
                />
                <FilterActions>
                    <Button type="submit" size="sm">
                        {t('movementMap.apply')}
                    </Button>
                    <Button
                        type="button"
                        size="sm"
                        variant={ButtonVariant.Outline}
                        onClick={() => {
                            setDraft({});
                            data.setFilters({});
                        }}
                    >
                        {t('movementMap.clear')}
                    </Button>
                </FilterActions>
            </FilterRow>
            {(loadError || data.error || data.dictionaryError) && (
                <p role="alert">{t('movementMap.loadError')}</p>
            )}
            {!loaded && !loadError && <Spinner />}
            {loaded && (
                <MapCanvas>
                    <GM
                        mapContainerStyle={containerStyle}
                        center={MAP_CENTER}
                        zoom={6}
                        onLoad={(value) => {
                            map.current = value;
                        }}
                        onUnmount={() => {
                            map.current = null;
                        }}
                        onIdle={() => {
                            if (map.current) data.onIdle(map.current);
                        }}
                        onClick={() => data.setSelected(null)}
                    >
                        {data.clusters.map((c) => {
                            const scale =
                                0.66 + 0.34 * Math.sqrt(Math.max(0, c.eventsCount) / maximum);
                            const size = 88 * scale;
                            return (
                                <Marker
                                    key={c.h3Index}
                                    position={{lat: c.latitude, lng: c.longitude}}
                                    icon={{
                                        url:
                                            'data:image/svg+xml;base64,' +
                                            btoa(markerSvg(c.events || [], size)),
                                        scaledSize: new google.maps.Size(size, size),
                                        anchor: new google.maps.Point(size / 2, size / 2),
                                    }}
                                    label={{
                                        text: c.eventsCount.toLocaleString('en-US', {
                                            notation: 'compact',
                                            maximumFractionDigits: 1,
                                        }),
                                        color: 'white',
                                        fontSize: Math.round(20 * scale) + 'px',
                                        fontWeight: 'bold',
                                    }}
                                    onClick={() => data.setSelected(c)}
                                />
                            );
                        })}
                    </GM>
                    <Legend>
                        <summary>{t('movementMap.legend')}</summary>
                        <ul>
                            {data.dictionaries.eventKinds.map(({code, title}) => (
                                <li key={code}>
                                    <span style={{color: eventColor(code)}} aria-hidden>
                                        ■
                                    </span>
                                    {eventLabel(code, title)}
                                </li>
                            ))}
                        </ul>
                    </Legend>
                    {data.pending && (
                        <div className="map-loading" role="status">
                            <Spinner />
                        </div>
                    )}
                    {data.selected && (
                        <Popup>
                            <Button
                                type="button"
                                size="sm"
                                variant={ButtonVariant.Outline}
                                onClick={() => data.setSelected(null)}
                            >
                                {t('movementMap.close')}
                            </Button>
                            <strong>
                                {t('movementMap.totalEvents')}: {count(data.selected.eventsCount)}
                            </strong>
                            {(data.selected.events || []).map((event) => (
                                <div key={event.eventKind}>
                                    <div>
                                        <span style={{color: eventColor(event.eventKind)}}>■ </span>
                                        {eventLabel(event.eventKind)}
                                    </div>
                                    <div>
                                        {t('movementMap.eventsCount', {
                                            count: event.eventsCount,
                                        })}
                                    </div>
                                    {['AGGREGATION', 'DEAGGREGATION'].includes(event.eventKind) && (
                                        <div>
                                            {t('movementMap.codesCount', {
                                                count: event.consumerCodes,
                                            })}
                                        </div>
                                    )}
                                </div>
                            ))}
                        </Popup>
                    )}
                </MapCanvas>
            )}
        </MapWrapper>
    );
};
